import React from "react";
import EntityForm from "../../EntityForm/EntityForm";
import { entityToFormValuesMapper } from "../../../Common/utils";
import { RESTRICTION_TYPE, WEEK_DAYS } from "../../../Common/constants";
import { defineMessages, injectIntl } from "react-intl";
import moment from "moment";

const messages = defineMessages({
  mustSetDateRange: {
    id: "app.entityForm.mustSetDateRange",
    description: "Date range must be set",
    defaultMessage: "Date range must be set",
  },
  startAfterEnd: {
    id: "app.closeTravelWindow.startAfterEnd",
    description: "Start date must be before end date",
    defaultMessage: "Start date must be before end date",
  },
  overlappingDates: {
    id: "app.closeTravelWindow.overlappingDates",
    description: "Overlapping close travel window dates",
    defaultMessage:
      "The selected dates overlap with an existing close travel window ({start} – {end}). Please choose a non-overlapping date range.",
  },
  noOverlapWithTravelWindow: {
    id: "app.closeTravelWindow.noOverlapWithTravelWindow",
    description: "Exclusion range does not overlap any open travel window",
    defaultMessage:
      "The exclusion range ({start} – {end}) does not overlap with any active Travel Window. This exclusion will have no effect.",
  },
});

function customEntityToFormValuesMapper({ entity, fields, locales, translationConfig }) {
  const formValues = entityToFormValuesMapper({
    entity,
    fields,
    locales,
    translationConfig,
  });

  const existingCloseTravel =
    formValues.restrictions &&
    formValues.restrictions[RESTRICTION_TYPE.CLOSE_TRAVEL] &&
    formValues.restrictions[RESTRICTION_TYPE.CLOSE_TRAVEL].length > 0;

  if (existingCloseTravel) {
    // Deep clone to avoid mutations affecting the original entity reference in the list
    formValues.restrictions = {
      ...formValues.restrictions,
      [RESTRICTION_TYPE.CLOSE_TRAVEL]: formValues.restrictions[
        RESTRICTION_TYPE.CLOSE_TRAVEL
      ].map(item => ({
        ...item,
        weekDays: Array.isArray(item.weekDays) ? [...item.weekDays] : [],
      })),
    };
  } else {
    formValues.restrictions = {
      [RESTRICTION_TYPE.CLOSE_TRAVEL]: [
        {
          startDate: null,
          endDate: null,
          weekDays: Object.values(WEEK_DAYS),
        },
      ],
    };
  }

  return {
    ...formValues,
  };
}

class PromotionsCloseTravelWindowForm extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {};
  }

  componentDidMount() {}

  componentWillUnmount() {}

  getFields(fields) {
    return fields && fields.form;
  }

  handleSubmit = ({ formValues }) => {
    const { id, ...otherFormValues } = formValues;

    const entity = otherFormValues;

    this.props.onSubmitEntity({
      entity,
    });
  };

  customValidate = formValues => {
    const restriction = formValues.restrictions[RESTRICTION_TYPE.CLOSE_TRAVEL][0];

    // 1. Both dates must be present
    if (!restriction.startDate || !restriction.endDate) {
      return this.props.intl.formatMessage(messages.mustSetDateRange);
    }

    // 2. Start date must not be after end date
    if (moment(restriction.startDate).isAfter(moment(restriction.endDate))) {
      return this.props.intl.formatMessage(messages.startAfterEnd);
    }

    // 3. No overlap with existing close travel windows
    const allEntities = this.props.allEntities || [];
    const currentTempId = this.props.entity && this.props.entity._tempId;

    for (const existingEntity of allEntities) {
      // Exclude the entity currently being edited
      if (currentTempId !== undefined && existingEntity._tempId === currentTempId) {
        continue;
      }

      const existing =
        existingEntity.restrictions &&
        existingEntity.restrictions[RESTRICTION_TYPE.CLOSE_TRAVEL] &&
        existingEntity.restrictions[RESTRICTION_TYPE.CLOSE_TRAVEL][0];

      if (!existing || !existing.startDate || !existing.endDate) {
        continue;
      }

      // Two ranges [s1,e1] and [s2,e2] overlap if s1 <= e2 AND s2 <= e1
      const overlaps =
        moment(restriction.startDate).isSameOrBefore(moment(existing.endDate)) &&
        moment(existing.startDate).isSameOrBefore(moment(restriction.endDate));

      if (overlaps) {
        return this.props.intl.formatMessage(messages.overlappingDates, {
          start: existing.startDate,
          end: existing.endDate,
        });
      }
    }

    // 4. The exclusion range must overlap with at least one open travel window
    const travelWindows =
      this.props.parentFormValues && this.props.parentFormValues.travelWindow;

    if (Array.isArray(travelWindows) && travelWindows.length > 0) {
      const overlapsWithAny = travelWindows.some(tw => {
        const openTravel =
          tw.restrictions &&
          tw.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL] &&
          tw.restrictions[RESTRICTION_TYPE.OPEN_TRAVEL][0];

        if (!openTravel || !openTravel.startDate || !openTravel.endDate) {
          return false;
        }

        return (
          moment(restriction.startDate).isSameOrBefore(moment(openTravel.endDate)) &&
          moment(openTravel.startDate).isSameOrBefore(moment(restriction.endDate))
        );
      });

      if (!overlapsWithAny) {
        return this.props.intl.formatMessage(messages.noOverlapWithTravelWindow, {
          start: restriction.startDate,
          end: restriction.endDate,
        });
      }
    }

    return "";
  };

  render() {
    const { config } = this.props;
    const { name, validation, fields, renderers, translation, entities } = config;
    const {
      locales,
      entity,
      entityError,
      entityResult,
      loading,
      getListPath,
      nested,
      afterSubmit,
      onCancelEdit,
      entityName,
    } = this.props;
    const { getFields, handleSubmit } = this;
    return (
      <EntityForm
        loading={loading}
        name={name}
        entityName={entityName}
        entity={entity}
        entities={entities}
        validation={validation}
        renderers={renderers}
        fields={getFields(fields)}
        locales={locales}
        translation={translation}
        onSubmit={handleSubmit}
        entityError={entityError}
        customValidate={this.customValidate}
        entityResult={entityResult}
        getListPath={getListPath}
        nested={nested}
        afterSubmit={afterSubmit}
        onCancelEdit={onCancelEdit}
        entityToFormValuesMapper={customEntityToFormValuesMapper}
      />
    );
  }
}

PromotionsCloseTravelWindowForm.propTypes = {};

PromotionsCloseTravelWindowForm.defaultProps = {};

export default injectIntl(PromotionsCloseTravelWindowForm);



